import {
  UseOfForceAuthorizationRequestDraftModel,
  UseOfForceHygieneRestraintLogModel,
  UseOfForceAuthorizationModel,
  UseOfForceAuthorizationRequestModel,
  UseOfForceAuthorizationType,
  UseOfForceAssignmentOptionModel,
  UseOfForceIncidentDraftModel,
  UseOfForceIncidentModel,
  UseOfForceLeaderSectionModel,
  UseOfForceSummaryRegistrationDraftModel,
  UseOfForceSummaryRegistrationModel,
  UseOfForceUploadedFile,
} from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import type { UseOfForceAuthorizationDecision } from '../../authority/use-of-force-assessment/use-of-force-authorization-api';
import type { UseOfForceAuthorizationRegistration } from './use-of-force-authorization-registration-dialog';

const useOfForceClient = moduleClientFactory.createCitizenServiceProviderUseOfForceClient();
const citizenClient = moduleClientFactory.createCitizenClient();

export async function getCitizenAddress(citizenId: string, serviceProviderUnitId: string): Promise<string | undefined> {
  const response = await citizenClient.getSocialProviderCitizenHeader({ citizenId, serviceProviderUnitId });
  return response.citizenHeader?.address;
}

export function getUseOfForceIncidents(citizenId: string, serviceProviderUnitId: string): Promise<UseOfForceIncidentModel[]> {
  return useOfForceClient
    .getUseOfForceIncidentsForCitizen({ citizenId, serviceProviderUnitId })
    .then(response => response.incidents ?? []);
}

export function getUseOfForceIncident(citizenId: string, serviceProviderUnitId: string, incidentId: string): Promise<UseOfForceIncidentModel> {
  return useOfForceClient
    .getUseOfForceIncident({ citizenId, serviceProviderUnitId, incidentId })
    .then(response => response.incident);
}

export function getUseOfForceAssignmentOptions(
  citizenId: string,
  serviceProviderUnitId: string
): Promise<UseOfForceAssignmentOptionModel[]> {
  return useOfForceClient
    .getUseOfForceAssignmentOptions({ citizenId, serviceProviderUnitId })
    .then(response => response.assignments ?? []);
}

export function createUseOfForceIncident(
  citizenId: string,
  serviceProviderUnitId: string,
  assignmentId: string | undefined
): Promise<UseOfForceIncidentModel> {
  return useOfForceClient
    .createUseOfForceIncident({ citizenId, serviceProviderUnitId, assignmentId })
    .then(response => response.incident);
}

export function updateUseOfForceIncidentDraft(
  citizenId: string,
  serviceProviderUnitId: string,
  incidentId: string,
  draft: UseOfForceIncidentDraftModel
): Promise<UseOfForceIncidentModel> {
  return useOfForceClient
    .updateUseOfForceIncidentDraft({ citizenId, serviceProviderUnitId, incidentId, draft })
    .then(response => response.incident);
}

export function deleteUseOfForceIncidentDraft(citizenId: string, serviceProviderUnitId: string, incidentId: string): Promise<void> {
  return useOfForceClient.deleteUseOfForceIncidentDraft({ citizenId, serviceProviderUnitId, incidentId }).then(() => undefined);
}

export function submitUseOfForceIncident(
  citizenId: string,
  serviceProviderUnitId: string,
  incidentId: string,
  draft: UseOfForceIncidentDraftModel
): Promise<UseOfForceIncidentModel> {
  return useOfForceClient
    .submitUseOfForceIncident({ citizenId, serviceProviderUnitId, incidentId, draft })
    .then(response => response.incident);
}

export interface UseOfForceIncidentKey {
  citizenId: string;
  serviceProviderUnitId: string;
  incidentId: string;
}

export async function updateUseOfForceIncidentLeaderSection(
  key: UseOfForceIncidentKey,
  leaderSection: UseOfForceLeaderSectionModel
): Promise<UseOfForceIncidentModel> {
  const response = await useOfForceClient.updateUseOfForceIncidentLeaderSection({ ...key, leaderSection });
  return response.incident;
}

export async function approveUseOfForceIncident(key: UseOfForceIncidentKey, leaderSection: UseOfForceLeaderSectionModel): Promise<UseOfForceIncidentModel> {
  const response = await useOfForceClient.approveUseOfForceIncident({ ...key, leaderSection });
  return response.incident;
}

export async function returnUseOfForceIncident(key: UseOfForceIncidentKey, comment: string): Promise<UseOfForceIncidentModel> {
  const response = await useOfForceClient.returnUseOfForceIncident({ ...key, comment });
  return response.incident;
}

export async function cancelUseOfForceIncident(key: UseOfForceIncidentKey, reason: string): Promise<UseOfForceIncidentModel> {
  const response = await useOfForceClient.cancelUseOfForceIncident({ ...key, reason });
  return response.incident;
}

export async function getUseOfForceIncidentPdf(key: UseOfForceIncidentKey): Promise<Blob> {
  const response = await useOfForceClient.getUseOfForceIncidentPdf(key);
  return response.data;
}

export async function attachCitizenStatement(key: UseOfForceIncidentKey, file: UseOfForceUploadedFile): Promise<UseOfForceIncidentModel> {
  const response = await useOfForceClient.attachUseOfForceCitizenStatement({ ...key, file });
  return response.incident;
}

export async function removeCitizenStatement(key: UseOfForceIncidentKey, statementId: string): Promise<UseOfForceIncidentModel> {
  const response = await useOfForceClient.removeUseOfForceCitizenStatement({ ...key, statementId });
  return response.incident;
}

export async function getCitizenStatement(key: UseOfForceIncidentKey, statementId: string): Promise<Blob> {
  const response = await useOfForceClient.getUseOfForceCitizenStatement({ ...key, statementId });
  return response.data;
}

export interface UseOfForceAuthorizationRequestKey {
  citizenId: string;
  serviceProviderUnitId: string;
  requestId: string;
}

export async function getUseOfForceAuthorizationRequests(
  citizenId: string,
  serviceProviderUnitId: string
): Promise<{ canAdminister: boolean; requests: UseOfForceAuthorizationRequestModel[] }> {
  const response = await useOfForceClient.getUseOfForceAuthorizationRequestsForCitizen({ citizenId, serviceProviderUnitId });
  return { canAdminister: response.canAdminister, requests: response.requests ?? [] };
}

export async function getUseOfForceAuthorizationRequest(key: UseOfForceAuthorizationRequestKey): Promise<UseOfForceAuthorizationRequestModel> {
  const response = await useOfForceClient.getUseOfForceAuthorizationRequest(key);
  return response.request;
}

export async function createUseOfForceAuthorizationRequest(citizenId: string, serviceProviderUnitId: string): Promise<UseOfForceAuthorizationRequestModel> {
  const response = await useOfForceClient.createUseOfForceAuthorizationRequest({ citizenId, serviceProviderUnitId });
  return response.request;
}

export async function createUseOfForceAuthorizationRequestForType(
  citizenId: string,
  serviceProviderUnitId: string,
  authorizationType: UseOfForceAuthorizationType
): Promise<UseOfForceAuthorizationRequestModel> {
  const request = await createUseOfForceAuthorizationRequest(citizenId, serviceProviderUnitId);
  return updateUseOfForceAuthorizationRequestDraft({ citizenId, serviceProviderUnitId, requestId: request.id }, { ...request.draft, authorizationType });
}

export async function updateUseOfForceAuthorizationRequestDraft(
  key: UseOfForceAuthorizationRequestKey,
  draft: UseOfForceAuthorizationRequestDraftModel
): Promise<UseOfForceAuthorizationRequestModel> {
  const response = await useOfForceClient.updateUseOfForceAuthorizationRequestDraft({ ...key, draft });
  return response.request;
}

export async function sendUseOfForceAuthorizationRequest(
  key: UseOfForceAuthorizationRequestKey,
  draft: UseOfForceAuthorizationRequestDraftModel
): Promise<UseOfForceAuthorizationRequestModel> {
  const response = await useOfForceClient.sendUseOfForceAuthorizationRequest({ ...key, draft });
  return response.request;
}

export async function registerUseOfForceAuthorizationRequestMunicipalitySending(
  key: UseOfForceAuthorizationRequestKey,
  sentAt: Date
): Promise<UseOfForceAuthorizationRequestModel> {
  const response = await useOfForceClient.registerUseOfForceAuthorizationRequestMunicipalitySending({ ...key, sentAt });
  return response.request;
}

export async function deleteUseOfForceAuthorizationRequestDraft(key: UseOfForceAuthorizationRequestKey): Promise<void> {
  await useOfForceClient.deleteUseOfForceAuthorizationRequestDraft(key);
}

export async function getUseOfForceAuthorizationRequestPdf(key: UseOfForceAuthorizationRequestKey): Promise<Blob> {
  const response = await useOfForceClient.getUseOfForceAuthorizationRequestPdf(key);
  return response.data;
}

export interface UseOfForceAuthorizationsAtUnit {
  authorizations: UseOfForceAuthorizationModel[];
  canAdminister: boolean;
  canRegister: boolean;
}

export async function getUseOfForceAuthorizationsAtUnit(citizenId: string, serviceProviderUnitId: string): Promise<UseOfForceAuthorizationsAtUnit> {
  const response = await useOfForceClient.getUseOfForceAuthorizationsForServiceProviderUnit({ citizenId, serviceProviderUnitId });
  return { authorizations: response.authorizations ?? [], canAdminister: response.canAdminister, canRegister: response.canRegister };
}

export async function registerUseOfForceAuthorizationAtUnit(
  citizenId: string,
  serviceProviderUnitId: string,
  registration: Omit<UseOfForceAuthorizationRegistration, 'serviceProviderUnitId'>
): Promise<UseOfForceAuthorizationModel> {
  const response = await useOfForceClient.registerServiceProviderUseOfForceAuthorization({ ...registration, citizenId, serviceProviderUnitId });
  return response.authorization;
}

export async function registerUseOfForceAuthorizationRequestDecision(
  key: UseOfForceAuthorizationRequestKey,
  decision: UseOfForceAuthorizationDecision
): Promise<UseOfForceAuthorizationRequestModel> {
  const response = await useOfForceClient.registerUseOfForceAuthorizationRequestDecision({ ...key, ...decision });
  return response.request;
}

export async function revokeUseOfForceAuthorizationAtUnit(
  citizenId: string,
  serviceProviderUnitId: string,
  authorizationId: string
): Promise<UseOfForceAuthorizationModel> {
  const response = await useOfForceClient.revokeServiceProviderUseOfForceAuthorization({ citizenId, serviceProviderUnitId, authorizationId });
  return response.authorization;
}

export interface UseOfForceSummaryRegistrationKey {
  citizenId: string;
  serviceProviderUnitId: string;
  registrationId: string;
}

export async function getUseOfForceSummaryRegistrations(
  citizenId: string,
  serviceProviderUnitId: string
): Promise<{ canAdminister: boolean; registrations: UseOfForceSummaryRegistrationModel[] }> {
  const response = await useOfForceClient.getUseOfForceSummaryRegistrationsForCitizen({ citizenId, serviceProviderUnitId });
  return { canAdminister: response.canAdminister, registrations: response.registrations ?? [] };
}

export async function getUseOfForceSummaryRegistration(key: UseOfForceSummaryRegistrationKey): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.getUseOfForceSummaryRegistration(key);
  return response.registration;
}

export async function createUseOfForceSummaryRegistration(
  citizenId: string,
  serviceProviderUnitId: string,
  authorizationId: string
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.createUseOfForceSummaryRegistration({ citizenId, serviceProviderUnitId, authorizationId });
  return response.registration;
}

export async function updateUseOfForceSummaryRegistrationDraft(
  key: UseOfForceSummaryRegistrationKey,
  draft: UseOfForceSummaryRegistrationDraftModel
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.updateUseOfForceSummaryRegistrationDraft({ ...key, draft });
  return response.registration;
}

export async function approveUseOfForceSummaryRegistration(
  key: UseOfForceSummaryRegistrationKey,
  draft: UseOfForceSummaryRegistrationDraftModel
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.approveUseOfForceSummaryRegistration({ ...key, draft });
  return response.registration;
}

export async function deleteUseOfForceSummaryRegistrationDraft(key: UseOfForceSummaryRegistrationKey): Promise<void> {
  await useOfForceClient.deleteUseOfForceSummaryRegistrationDraft(key);
}

export async function getUseOfForceSummaryRegistrationPdf(key: UseOfForceSummaryRegistrationKey): Promise<Blob> {
  const response = await useOfForceClient.getUseOfForceSummaryRegistrationPdf(key);
  return response.data;
}

export async function registerUseOfForceSummaryRegistrationMunicipalitySending(
  key: UseOfForceSummaryRegistrationKey,
  sentAt: Date
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.registerUseOfForceSummaryRegistrationMunicipalitySending({ ...key, sentAt });
  return response.registration;
}

export async function registerUseOfForceSummaryRegistrationSupervisionSending(
  key: UseOfForceSummaryRegistrationKey,
  sentAt: Date
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.registerUseOfForceSummaryRegistrationSupervisionSending({ ...key, sentAt });
  return response.registration;
}

export async function registerUseOfForceSummaryRegistrationOperatorInformation(
  key: UseOfForceSummaryRegistrationKey,
  informedOn: string
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.registerUseOfForceSummaryRegistrationOperatorInformation({ ...key, informedOn });
  return response.registration;
}

export async function registerUseOfForceSummaryRegistrationRelativesInformation(
  key: UseOfForceSummaryRegistrationKey,
  informedOn: string | undefined,
  notInformed: boolean
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.registerUseOfForceSummaryRegistrationRelativesInformation({ ...key, informedOn, notInformed });
  return response.registration;
}

export interface UseOfForceHygieneRestraintLogs {
  canAdminister: boolean;
  logs: UseOfForceHygieneRestraintLogModel[];
  currentUserName: string;
}

export interface UseOfForceHygieneRestraintInput {
  startedAt: Date;
  endedAt: Date;
  employeeName: string;
}

export async function getUseOfForceHygieneRestraintLogs(citizenId: string, serviceProviderUnitId: string): Promise<UseOfForceHygieneRestraintLogs> {
  const response = await useOfForceClient.getUseOfForceHygieneRestraintLogsForCitizen({ citizenId, serviceProviderUnitId });
  return { logs: response.logs ?? [], currentUserName: response.currentUserName, canAdminister: response.canAdminister };
}

export async function registerUseOfForceHygieneRestraint(
  citizenId: string,
  serviceProviderUnitId: string,
  restraint: UseOfForceHygieneRestraintInput
): Promise<UseOfForceHygieneRestraintLogModel> {
  const response = await useOfForceClient.registerUseOfForceHygieneRestraint({ citizenId, serviceProviderUnitId, ...restraint });
  return response.log;
}

export async function correctUseOfForceHygieneRestraint(
  citizenId: string,
  serviceProviderUnitId: string,
  logId: string,
  restraintId: string,
  restraint: UseOfForceHygieneRestraintInput
): Promise<UseOfForceHygieneRestraintLogModel> {
  const response = await useOfForceClient.correctUseOfForceHygieneRestraint({ citizenId, serviceProviderUnitId, logId, restraintId, ...restraint });
  return response.log;
}

export async function removeUseOfForceHygieneRestraint(citizenId: string, serviceProviderUnitId: string, logId: string, restraintId: string): Promise<void> {
  await useOfForceClient.removeUseOfForceHygieneRestraint({ citizenId, serviceProviderUnitId, logId, restraintId });
}

export async function approveUseOfForceHygieneRestraintLog(citizenId: string, serviceProviderUnitId: string, logId: string): Promise<UseOfForceHygieneRestraintLogModel> {
  const response = await useOfForceClient.approveUseOfForceHygieneRestraintLog({ citizenId, serviceProviderUnitId, logId });
  return response.log;
}

export async function registerUseOfForceHygieneRestraintLogMunicipalitySending(
  citizenId: string,
  serviceProviderUnitId: string,
  logId: string,
  sentAt: Date
): Promise<UseOfForceHygieneRestraintLogModel> {
  const response = await useOfForceClient.registerUseOfForceHygieneRestraintLogMunicipalitySending({ citizenId, serviceProviderUnitId, logId, sentAt });
  return response.log;
}

export async function openUseOfForceHygieneRestraintLogPdf(citizenId: string, serviceProviderUnitId: string, logId: string): Promise<void> {
  const response = await useOfForceClient.getUseOfForceHygieneRestraintLogPdf({ citizenId, serviceProviderUnitId, logId });
  window.open(URL.createObjectURL(response.data), '_blank');
}

export async function attachUseOfForceSummaryRegistrationCitizenStatement(
  key: UseOfForceSummaryRegistrationKey,
  file: UseOfForceUploadedFile
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.attachUseOfForceSummaryRegistrationCitizenStatement({ ...key, file });
  return response.registration;
}

export async function removeUseOfForceSummaryRegistrationCitizenStatement(
  key: UseOfForceSummaryRegistrationKey,
  statementId: string
): Promise<UseOfForceSummaryRegistrationModel> {
  const response = await useOfForceClient.removeUseOfForceSummaryRegistrationCitizenStatement({ ...key, statementId });
  return response.registration;
}

export async function getUseOfForceSummaryRegistrationCitizenStatement(key: UseOfForceSummaryRegistrationKey, statementId: string): Promise<Blob> {
  const response = await useOfForceClient.getUseOfForceSummaryRegistrationCitizenStatement({ ...key, statementId });
  return response.data;
}
